import { useEffect, useRef, useState } from "react";

const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;

/**
 * Encapsulates the Square Web Payments SDK lifecycle:
 *  - initializes the secure Card field (once, on mount)
 *  - initializes Apple Pay / Google Pay wallets (re-runs when the amount changes)
 *  - exposes tokenize helpers for card + wallet payments
 *
 * Lifecycle hooks are intentionally scoped: the card field mounts once, while
 * the wallet payment-request must be rebuilt whenever the charge amount changes.
 */
export function useSquarePayments({ items, finalDueToday }) {
  const cardContainerRef = useRef(null);
  const cardRef = useRef(null);
  const paymentsRef = useRef(null);
  const applePayRef = useRef(null);
  const googlePayRef = useRef(null);

  const [squareReady, setSquareReady] = useState(false);
  const [squareError, setSquareError] = useState(null);
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  const [googlePayAvailable, setGooglePayAvailable] = useState(false);

  // ---- Card field: initialize once on mount ----
  useEffect(() => {
    let mounted = true;
    let cardInstance = null;

    const init = async () => {
      if (!window.Square) {
        setSquareError("Payment system failed to load. Please refresh the page.");
        return;
      }
      if (!SQUARE_APP_ID || !SQUARE_LOCATION_ID) {
        setSquareError("Payment system is not configured. Please contact support.");
        return;
      }
      try {
        const payments = window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
        paymentsRef.current = payments;
        cardInstance = await payments.card({
          style: {
            input: { fontSize: "15px", fontFamily: "Arial, sans-serif", color: "#0b1c5c" },
            ".input-container": { borderColor: "#e2e8f0", borderRadius: "10px" },
            ".input-container.is-focus": { borderColor: "#0b1c5c" },
            ".input-container.is-error": { borderColor: "#dc2626" },
            ".message-text": { color: "#dc2626" },
            ".message-icon": { color: "#dc2626" },
          }
        });
        if (!mounted) {
          try { cardInstance.destroy && cardInstance.destroy(); } catch (e) { console.warn("Square card cleanup failed", e); }
          return;
        }
        // Clear any leftover iframe before attaching (StrictMode double-mount safety)
        const container = document.getElementById("square-card-container");
        if (container) container.replaceChildren();
        await cardInstance.attach("#square-card-container");
        if (!mounted) {
          try { cardInstance.destroy && cardInstance.destroy(); } catch (e) { console.warn("Square card cleanup failed", e); }
          return;
        }
        cardRef.current = cardInstance;
        setSquareReady(true);
      } catch (e) {
        console.error("Square init error", e);
        if (mounted) setSquareError("Could not initialize secure card field. Please refresh.");
      }
    };

    init();
    return () => {
      mounted = false;
      try { cardInstance && cardInstance.destroy && cardInstance.destroy(); } catch (e) { console.warn("Square card cleanup failed", e); }
      cardRef.current = null;
    };
  }, []);

  // ---- Apple Pay + Google Pay: rebuild when amount/items change ----
  useEffect(() => {
    const payments = paymentsRef.current;
    if (!payments || !items.length) return;
    let cancelled = false;
    let ap = null;
    let gp = null;

    const buildRequest = () => {
      const lineItems = items.map(it => {
        const qty = it.qty || 1;
        const lineAmt = ((it.setupFee || 0) + (it.oneTime || 0)) * qty;
        return { label: `${it.name}${qty > 1 ? ` x${qty}` : ""}`, amount: lineAmt.toFixed(2) };
      }).filter(li => parseFloat(li.amount) > 0);
      const baseAmt = items.reduce((s, it) => s + ((it.setupFee || 0) + (it.oneTime || 0)) * (it.qty || 1), 0);
      const discountAmt = Math.max(0, baseAmt - finalDueToday);
      if (discountAmt > 0) {
        lineItems.push({ label: "Promo discount", amount: (-discountAmt).toFixed(2) });
      }
      return payments.paymentRequest({
        countryCode: "US",
        currencyCode: "USD",
        total: { label: "Knight Credit Consultants", amount: finalDueToday.toFixed(2) },
        lineItems,
      });
    };

    const initWallets = async () => {
      try {
        const req = buildRequest();
        // Apple Pay
        try {
          ap = await payments.applePay(req);
          if (!cancelled) {
            applePayRef.current = ap;
            setApplePayAvailable(true);
          }
        } catch (e) {
          applePayRef.current = null;
          setApplePayAvailable(false);
        }
        // Google Pay
        try {
          gp = await payments.googlePay(req);
          if (!cancelled) {
            await gp.attach("#google-pay-button", {
              buttonColor: "black",
              buttonType: "long",
              buttonSizeMode: "fill",
            });
            googlePayRef.current = gp;
            setGooglePayAvailable(true);
          }
        } catch (e) {
          googlePayRef.current = null;
          setGooglePayAvailable(false);
        }
      } catch (e) {
        console.error("Wallet init error", e);
      }
    };

    initWallets();

    return () => {
      cancelled = true;
      try { ap && ap.destroy && ap.destroy(); } catch (e) { console.warn("Apple Pay cleanup failed", e); }
      try { gp && gp.destroy && gp.destroy(); } catch (e) { console.warn("Google Pay cleanup failed", e); }
      applePayRef.current = null;
      googlePayRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [squareReady, finalDueToday, items.length]);

  // Tokenize the entered card → returns Square tokenize result.
  const tokenizeCard = async () => {
    if (!cardRef.current) return { status: "ERROR", errors: [{ message: "Card field not ready" }] };
    return cardRef.current.tokenize();
  };

  // Tokenize a wallet payment ('apple' | 'google') → returns Square tokenize result.
  const tokenizeWallet = async (which) => {
    const wallet = which === "apple" ? applePayRef.current : googlePayRef.current;
    if (!wallet) return null;
    return wallet.tokenize();
  };

  return {
    cardContainerRef,
    squareReady,
    squareError,
    applePayAvailable,
    googlePayAvailable,
    tokenizeCard,
    tokenizeWallet,
  };
}
